/**
 * Geometric measurement step: the microscope photos behind the widths.
 *
 * One microscope photo per patch group, with its corners aligned, sent together
 * before width measurement runs. Continuing once for all groups keeps the
 * iteration in a single consistent state.
 */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Stack,
    Button,
    Modal,
    Box as MuiBox,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    LinearProgress,
    CircularProgress,
    Paper,
    Alert,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import LaserMarkPanel from "../../components/LaserMarkPanel";
import MarkingOptionsBox from "../../components/MarkingOptionsBox";
import { markingTimeFromHeaders } from "../../helper/markingTime";
import CameraCapturePanel from "../../components/CameraCapturePanel";
import NavBarBottom, { navBottomButtonSx } from "../../components/NavBarBottom";
import { useExplorationParams } from "../../helper/entityRoutes";
import { EXPLORATION_LIST_ROUTE } from "../../helper/explorationRoutes";
import useIterationState from "../../helper/useIterationState";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import IterationExitDialog from "../../components/IterationExitDialog";
import Box from "@mui/material/Box";
import PreviewIcon from "@mui/icons-material/Preview";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import UndoIcon from "@mui/icons-material/Undo";
import axios from "axios";
import { naturalToDisplayedImageCoords, displayedToNaturalImageCoords } from "../../helper/imageOverlayCoordinates";
import { openMarkingPreviewWindow, getFilenameFromContentDisposition } from "../../helper/markingPreviewWindow";
import {
  WIDTH_PATCH_CACHE_KEY,
  buildWidthPatchCache,
} from "../../helper/widthPatchCache";
import {
    EXPLORATION_FLOW_STEPS,
    explorationIdLast4,
    populationCodeForPhase,
    populationLabelForPhase,
} from "../../helper/colorExplorationParameters";
import {
    restartIteration,
    revertIterationWidthMeasurement,
} from "../../helper/iterationFlowControls";

const actionButtonSx = {
    textTransform: "none",
    borderRadius: 2,
    px: 2.5,
    py: 1,
    fontWeight: 600,
    boxShadow: "none",
    "&:hover": { boxShadow: 1 },
};

const dialogButtonSx = {
    ...actionButtonSx,
    px: 1.75,
    py: 0.6,
    minHeight: 34,
    fontSize: "0.82rem",
};

const stepCardSx = {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    m: 0,
    boxSizing: "border-box",
    p: { xs: 2, sm: 2.5 },
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.paper",
};

/**
 * Visually mute a step card and block all pointer interactions while the
 * backend is still preparing the next iteration. Without this, the
 * Download / Preview / Choose-image controls look fully active even though
 * the marking file isn't ready yet, which previously caused failed clicks.
 */

function emptyGeometricSlot() {
    return {
        previewUrl: "",
        sourceFile: null,
        /** name of the file already stored on the server by api/exploration/photo (one round-trip) */
        uploadedFileName: null,
        fileExtension: "",
        scalingFactors: null,
        cornerPoints: [],
        previewLoading: false,
        previewMessage: "",
    };
}

export default function ColorExplorationColorResolution() {
    const [geometricSlots, setGeometricSlots] = useState([]);
    const geometricSlotsRef = useRef(geometricSlots);
    geometricSlotsRef.current = geometricSlots;

    const slotImageRefs = useRef([]);

    const [loading, setLoading] = useState(false);
    /** Which patch-group slot is open in the corner-adjustment modal (null = closed). */
    const [modalSlotIndex, setModalSlotIndex] = useState(null);
    const modalImageRef = useRef(null);
    /** Bumped after modal image loads / resizes so corner overlays track object-fit: contain layout correctly. */
    const [modalImageLayoutTick, setModalImageLayoutTick] = useState(0);
    /** Bumped when any slot thumbnail `<img>` loads so corner dots use correct natural dimensions. */
    const [, setThumbnailLayoutTick] = useState(0);
    /** Marking ZIP download — inline spinner next to button, not full-screen */
    const [downloadMarkingLoading, setDownloadMarkingLoading] = useState(false);
    const [previewMarkingLoading, setPreviewMarkingLoading] = useState(false);
    const [automaticCornerDetectionBusy, setAutomaticCornerDetectionBusy] = useState(false);
    const [redoMeasurement, setRedoMeasurement] = useState(false);
    const [rollbackBusy, setRollbackBusy] = useState(false);
    const [rollbackError, setRollbackError] = useState(null);
    const [totalBatches, setTotalBatches] = useState(0);
    const [currentPatchIndex, setCurrentPatchIndex] = useState(0);
    // The exploration is the URL's; the running iteration is fetched for it
    // (the sidebar shows the user-facing number: P_0 and Q_0 both belong to
    // iteration 0, only Q_n bumps it to n; the backend is keyed by the
    // internal number). Nothing is shown until the fetch answers. The Results
    // page prepares the next generation before it opens this page, so the
    // iteration reported here is the one to mark.
    const { explorationId, stepRoute } = useExplorationParams();
    const { iteration, iterationRef, applyIterationResponse } = useIterationState();
    const explorationIteration = iteration.display;
    const iterationPhase = iteration.phase;
    const [markingTime, setMarkingTime] = useState(null);
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const navigate = useNavigate();
    const pendingNextPageRef = useRef(null);
    /** Batch index when upload validation failed (for “continue anyway” retry). */
    const failedUploadBatchRef = useRef(0);
    const [showWrongMeasurementData, setShowWrongMeasurementData] = useState(false);
    /** Set when api/upload/file-to-cloud returns Success: false (e.g. OCR could not confirm [iteration][batch]) */
    const [uploadValidationError, setUploadValidationError] = useState(null);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    /**
     * Visible error from the cluster-image submit pipeline (corner upload
     * + measure_width). Used to be a silent console.error which left the
     * user looking at a re-set form with no feedback. Three flavours:
     *   - timedOut: nginx / network gave up while measure_width was still
     *     running. We poll patch_group state and recover when the backend
     *     finishes.
     *   - precondition: backend rejected with HTTP 400 because the patch
     *     groups are not in the expected state for this step.
     *   - server: anything else (HTTP 5xx, unexpected response).
     */
    const [submitError, setSubmitError] = useState(null);
    const [submitRecoveryBusy, setSubmitRecoveryBusy] = useState(false);

    const updateGeometricSlot = (batchIndex, partial) => {
        setGeometricSlots((prev) => {
            const next = [...prev];
            const cur = next[batchIndex] ?? emptyGeometricSlot();
            next[batchIndex] = { ...cur, ...partial };
            return next;
        });
    };

    // Handle coordinate clicks inside the manual adjustment modal
    const handleImageClick = (event) => {
        const image = modalImageRef.current;
        if (!image || modalSlotIndex == null) return;
        const imageRect = image.getBoundingClientRect();
        const x = event.clientX - imageRect.left;
        const y = event.clientY - imageRect.top;

        const { x: x_a, y: y_a } = displayedToNaturalImageCoords(image, x, y);

        const newCoordinates = { x_a, y_a };
        const slot = geometricSlotsRef.current[modalSlotIndex];
        const prevCorners = slot?.cornerPoints ?? [];
        if (prevCorners.length >= 4) return;
        const cornerPoints = [...prevCorners, newCoordinates];
        updateGeometricSlot(modalSlotIndex, { cornerPoints });
    };

    const handleClearCornerPoints = () => {
        if (modalSlotIndex == null) return;
        updateGeometricSlot(modalSlotIndex, { cornerPoints: [] });
    };

    const bumpModalImageLayout = () => setModalImageLayoutTick((v) => v + 1);

    const handleAutomaticCornerDetection = async () => {
        if (modalSlotIndex == null) return;
        const slot = geometricSlotsRef.current[modalSlotIndex];
        const file = slot?.sourceFile;
        const ext = slot?.fileExtension;
        if (!file || !(file instanceof File) || !ext) return;
        const xScale = parseFloat(slot?.scalingFactors?.xScale);
        const yScale = parseFloat(slot?.scalingFactors?.yScale);
        if (Number.isNaN(xScale) || Number.isNaN(yScale)) return;

        setAutomaticCornerDetectionBusy(true);
        try {
            if (slot.uploadedFileName !== file.name) {
                throw new Error("The microscope photo is not stored on the server; pick the file again.");
            }
            // the photo is on the server already: re-detect there, nothing travels
            const res = await axios.post("api/exploration/photo/corners", {
                explorationId: explorationId,
                fileName: `clusterImg_${modalSlotIndex}.${ext}`,
                batch: modalSlotIndex,
            }, { timeout: 600000 });
            const corners = (res.data?.cornerPoints || []).map((point) => ({
                x_a: point[0] / xScale, y_a: point[1] / yScale,
            }));
            updateGeometricSlot(modalSlotIndex, { cornerPoints: corners });
        } catch (error) {
            console.error("Automatic corner detection failed:", error);
        } finally {
            setAutomaticCornerDetectionBusy(false);
        }
    };

    const handleMicroscopeSlotImageChange = async (batchIndex, event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        await processMicroscopeFileForSlot(batchIndex, file);
    };

    // The cluster photo arrives as a File either from the picker or from the
    // camera agent capture; both flow through the same conversion + corner
    // detection (mirrors processCameraColorFileForSlot on the color page).
    /**
     * ONE round-trip: api/exploration/photo stores the file at its final
     * location (clusterImg_<batch>, what measure_width reads), detects the
     * patch corners on the stored file and answers with the preview.
     * Returns false when the endpoint declined, so the caller falls back to
     * "continue anyway" stores it again with the check skipped.
     */
    const storeMicroscopePhotoForSlot = async (batchIndex, file, ext, skipIdentifierValidation = false) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("explorationId", explorationId);
        formData.append("fileName", `clusterImg_${batchIndex}.${ext}`);
        formData.append("batch", String(batchIndex));
        if (skipIdentifierValidation) formData.append("skipIdentifierValidation", "true");
        const response = await axios.post("api/exploration/photo", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 900000,
        });
        if (!response.data?.Success || !response.data.image) {
            if (response.data && response.data.Success === false && response.data.Expected != null) {
                failedUploadBatchRef.current = batchIndex;
                setUploadValidationError(response.data);
                setShowWrongMeasurementData(true);
            }
            return false;
        }
        const { image, scaling_factors, cornerPoints } = response.data;
        const binaryImage = new Uint8Array(image.split("").map((char) => char.charCodeAt(0)));
        const blobUrl = URL.createObjectURL(new Blob([binaryImage], { type: "image/png" }));
        const xScaleRaw = parseFloat(scaling_factors?.x_scale);
        const yScaleRaw = parseFloat(scaling_factors?.y_scale);
        const xScale = Number.isFinite(xScaleRaw) ? xScaleRaw : 1;
        const yScale = Number.isFinite(yScaleRaw) ? yScaleRaw : 1;
        updateGeometricSlot(batchIndex, {
            previewUrl: blobUrl,
            scalingFactors: { xScale, yScale },
            cornerPoints: (cornerPoints || []).map((point) => ({ x_a: point[0] / xScale, y_a: point[1] / yScale })),
            uploadedFileName: file.name,
        });
        return true;
    };

    const processMicroscopeFileForSlot = async (batchIndex, file) => {
        updateGeometricSlot(batchIndex, {
            previewLoading: true,
            previewMessage: "Uploading and detecting patch corners…",
            cornerPoints: [],
            uploadedFileName: null,
        });

        const fileName = file.name;
        const ext = fileName.split(".").pop() || "";

        updateGeometricSlot(batchIndex, {
            fileExtension: ext,
            sourceFile: file,
            previewUrl: URL.createObjectURL(file),
        });

        try {
            // ONE round-trip: the photo is stored, the corners are detected on it and
            // the preview comes back. There is no other path.
            if (!(await storeMicroscopePhotoForSlot(batchIndex, file, ext))) {
                updateGeometricSlot(batchIndex, {
                    previewMessage: "The photo could not be stored; pick the file again.",
                    scalingFactors: { xScale: 1, yScale: 1 },
                });
            }
        } catch (error) {
            console.error("Storing the microscope photo failed:", error);
            // No scaling data exists for this slot; the raw-file preview is still shown,
            // identity mapping lets Next unblock once four manual corners are set.
            updateGeometricSlot(batchIndex, { scalingFactors: { xScale: 1, yScale: 1 } });
        } finally {
            updateGeometricSlot(batchIndex, { previewLoading: false, previewMessage: "" });
        }
    };

    const handleCloseModal = () => {
        setModalSlotIndex(null);
    };

    /** Upload one cluster image, then run width extraction for that patch group. */
    const uploadCloudAndMeasureForBatch = async (batchIndex, skipIdentifierValidation) => {
        const slot = geometricSlotsRef.current[batchIndex];
        if (!slot?.sourceFile || !slot.fileExtension || !slot.scalingFactors || slot.cornerPoints.length !== 4) {
            throw new Error("Incomplete geometric slot");
        }
        const { xScale, yScale } = slot.scalingFactors;
        const formData = new FormData();
        formData.append("file", slot.sourceFile);
        formData.append("explorationId", explorationId);
        formData.append("fileName", `clusterImg_${batchIndex}.${slot.fileExtension}`);
        formData.append("batch", String(batchIndex));
        if (skipIdentifierValidation) {
            formData.append("skipIdentifierValidation", "true");
        }

        // Already stored by api/exploration/photo when it was picked: only the
        // corner points travel. Otherwise the legacy upload.
        const alreadyStored = Boolean(slot.uploadedFileName) && slot.uploadedFileName === slot.sourceFile.name;
        if (!alreadyStored) {
            const uploadRes = await axios.post("api/upload/file-to-cloud", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (!uploadRes.data?.Success) {
                failedUploadBatchRef.current = batchIndex;
                setUploadValidationError(uploadRes.data ?? null);
                setShowWrongMeasurementData(true);
                return false;
            }
        }

        const resizedCornerPoints = slot.cornerPoints.map((point) => ({
            x_a: point.x_a * xScale,
            y_a: point.y_a * yScale,
        }));
        const request_data = {
            iterationNumber: iterationRef.current.number,
            explorationId: explorationId,
            cornerPoints: resizedCornerPoints.map((point) => [point.x_a, point.y_a]),
            batchNumber: batchIndex,
        };
        await axios.post("api/exploration/measure/width", request_data);
        return true;
    };

    /**
     * After a measure_width call apparently failed on the wire (timeout,
     * network blip, 504), the backend may already be done. Poll the
     * patch-group status endpoint a handful of times to confirm before
     * deciding whether to surface an error or just continue.
     */
    const pollMeasureWidthCompletion = async ({ attempts = 12, intervalMs = 5000 } = {}) => {
        for (let i = 0; i < attempts; i++) {
            try {
                const statusResp = await axios.post("api/patch/index/detail", {
                    explorationId: explorationId,
                    iterationNumber: iterationRef.current.number,
                    isWidthPatchGroup: true,
                });
                if (statusResp?.data?.measured) {
                    return true;
                }
            } catch (pollError) {
                console.warn("pollMeasureWidthCompletion: status check failed", pollError);
            }
            await sleep(intervalMs);
        }
        return false;
    };

    const submitAllGeometricBatchesAndNavigate = async (navigatePageName, startBatchIndex, skipIdentifierValidation) => {
        pendingNextPageRef.current = navigatePageName;
        const slots = geometricSlotsRef.current;
        if (!slots.length) return;

        setLoading(true);
        setShowWrongMeasurementData(false);
        setUploadValidationError(null);
        setSubmitError(null);
        try {
            const first = Math.max(0, Math.min(startBatchIndex, slots.length - 1));
            for (let batchIndex = first; batchIndex < slots.length; batchIndex++) {
                const ok = await uploadCloudAndMeasureForBatch(batchIndex, skipIdentifierValidation);
                if (!ok) {
                    setLoading(false);
                    return;
                }
            }
            const widthRes = await axios.post("api/exploration/width/results/data", {
                explorationId: explorationId,
                iterationNumber: iterationRef.current.number,
            });
            const results = widthRes.data?.results ?? [];
            localStorage.setItem(
                WIDTH_PATCH_CACHE_KEY,
                buildWidthPatchCache(
                    explorationId,
                    iterationRef.current.number,
                    results
                )
            );
            navigate(navigatePageName);
        } catch (error) {
            console.error("Geometric submit failed:", error);
            const status = error?.response?.status ?? null;
            const code = error?.response?.data?.code ?? null;
            const isTimeoutOrNetwork =
                error?.code === "ECONNABORTED" ||
                /Network Error/i.test(error?.message ?? "") ||
                status === 504 ||
                status === 502;
            const message =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                "Unexpected error during cluster measurement.";
            if (isTimeoutOrNetwork) {
                // measure_width may have actually completed server-side (only
                // the proxy gave up). Poll for the real outcome before
                // surfacing the error.
                setSubmitRecoveryBusy(true);
                const finished = await pollMeasureWidthCompletion();
                setSubmitRecoveryBusy(false);
                if (finished) {
                    try {
                        const widthRes = await axios.post("api/exploration/width/results/data", {
                            explorationId: explorationId,
                            iterationNumber: iterationRef.current.number,
                        });
                        const results = widthRes.data?.results ?? [];
                        localStorage.setItem(
                            WIDTH_PATCH_CACHE_KEY,
                            buildWidthPatchCache(
                                explorationId,
                                iterationRef.current.number,
                                results
                            )
                        );
                        navigate(navigatePageName);
                        return;
                    } catch (pollFollowupError) {
                        console.error(
                            "submitAllGeometricBatchesAndNavigate: results fetch after poll succeeded but follow-up failed",
                            pollFollowupError
                        );
                    }
                }
                setSubmitError({
                    flavour: "timedOut",
                    status,
                    code,
                    message,
                });
            } else if (status === 400) {
                setSubmitError({
                    flavour: "precondition",
                    status,
                    code,
                    message,
                });
            } else {
                setSubmitError({
                    flavour: "server",
                    status,
                    code,
                    message,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSubmitError = () => setSubmitError(null);
    const handleRetrySubmit = async () => {
        setSubmitError(null);
        await submitAllGeometricBatchesAndNavigate(
            pendingNextPageRef.current || stepRoute("width"),
            0,
            false
        );
    };

    const nextPageClickFunc = async (navigatePageName) => {
        await submitAllGeometricBatchesAndNavigate(navigatePageName, 0, false);
    };

    /**
     * Cancel used to exit silently, which left any partially measured
     * patch groups in the DB and routed the user to the wrong follow-up
     * step on the next "Continue". Now we open the iteration-exit
     * dialog so the user has to either continue editing or explicitly
     * discard the iteration's cluster width measurements before leaving.
     */
    const handleCancelToExplorationsList = () => {
        setCancelDialogOpen(true);
    };

    const handleCloseCancelDialog = () => {
        setCancelDialogOpen(false);
    };

    const handleDiscardAndExit = async () => {
        try {
            await revertIterationWidthMeasurement(
                explorationId,
                iterationRef.current.number
            );
        } catch (error) {
            console.error("Discard cluster measurement failed:", error);
        }
        setCancelDialogOpen(false);
        navigate(EXPLORATION_LIST_ROUTE);
    };

    const handleContinueWithoutIdentifierCheck = async () => {
        setShowWrongMeasurementData(false);
        setUploadValidationError(null);
        const start = failedUploadBatchRef.current;
        const declined = geometricSlotsRef.current?.[start];
        if (declined?.sourceFile && declined.uploadedFileName !== declined.sourceFile.name) {
            // the photo endpoint declined the identifier: store it again with the check skipped
            try {
                await storeMicroscopePhotoForSlot(start, declined.sourceFile, declined.fileExtension, true);
            } catch (error) {
                console.error("Storing the microscope photo with the check skipped failed:", error);
            }
        }
        submitAllGeometricBatchesAndNavigate(
            pendingNextPageRef.current,
            failedUploadBatchRef.current,
            true
        );
    };

    const handleOpenRedoMeasurementDialog = () => {
        setRollbackError(null);
        setRedoMeasurement(true);
    };

    const handleCloseRedoMeasurementDialog = () => {
        if (rollbackBusy) return;
        setRollbackError(null);
        setRedoMeasurement(false);
    };

    const handleCloseErrorDialog = () => {
        setShowWrongMeasurementData(false);
        setUploadValidationError(null);
    };

    /**
     * "Rollback last iteration" — the user wants to throw away the most
     * recently completed iteration entirely, including its uploaded
     * cluster image, and restart it from the top. The previous version
     * only reverted the *color* measurements of N-1 and then routed the
     * user to the Color Measurement page, leaving N-1's width
     * measurements (and the wrong cluster photograph) in the DB. We now
     * call ``restartIteration`` to revert *both* color and width on
     * iteration N-1, then refresh the Geometric page so it re-fetches
     * the (now-decremented) iteration number, drops cached corner
     * points / preview images, and lands the user on the upload step
     * for that iteration.
     */
    const handleConfirmRedoMeasurement = async () => {
        const currentIteration = Number(iterationRef.current.number);
        if (!Number.isFinite(currentIteration) || currentIteration < 1) {
            setRedoMeasurement(false);
            return;
        }
        const targetIteration = currentIteration - 1;
        setRollbackBusy(true);
        setRollbackError(null);
        try {
            await restartIteration({
                explorationId,
                iterationNumber: targetIteration,
                hasColorWork: true,
            });
            // The Width page caches its grid input here; the cached value
            // is now stale (it described the next iteration we just
            // discarded), so wipe it before reloading.
            localStorage.removeItem(WIDTH_PATCH_CACHE_KEY);
            // A reload is the simplest way to re-fetch iterationNumber /
            // iterationPhase / patch groups from scratch and reset every
            // local component state (corner points, slot previews, etc.)
            // without manually unwinding it all.
            window.location.reload();
        } catch (error) {
            console.error("Rollback last iteration failed:", error);
            setRollbackBusy(false);
            setRollbackError(
                "The rollback failed on the server and was stopped. Reload " +
                    "the page to see the actual iteration state, then retry " +
                    "the rollback. If it keeps failing, stop here and report " +
                    "it — do not continue marking."
            );
        }
    };


    const handleDownloadClick = async () => {
        setDownloadMarkingLoading(true);
        try {
            await sleep(3000); // Sleep for 3 seconds
            const data = {
                explorationId: explorationId,
                patchType: "SINGLE_CLUSTER",
                iterationNumber: iterationRef.current.number,
            };
            const response = await axios.post("api/download/marking/file", data);
            setMarkingTime((current) => markingTimeFromHeaders(response.headers) || current);
            const id4 = explorationIdLast4(explorationId);
            // Match the backend's "Pn / Qn" filename convention so the
            // downloaded file matches the label printed on the physical plate.
            // Falls back to the internal iteration number only if phase is
            // somehow unknown (e.g. very stale localStorage).
            const populationCode =
                populationCodeForPhase(
                    iterationRef.current.phase,
                    iterationRef.current.display
                ) || `iteration_${iterationRef.current.number}`;
            const filename =
                getFilenameFromContentDisposition(response.headers?.["content-disposition"]) ||
                `cluster_marking_file_${populationCode}_${id4}.json`;
            const jsonText = JSON.stringify(response.data, null, 2);
            const url = window.URL.createObjectURL(new Blob([jsonText], { type: "application/json" }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading marking files", error);
        } finally {
            setDownloadMarkingLoading(false);
        }
    };

    // Fetches the same marking JSON as the download button, but as a Blob so
    // it can be pushed to the local laser agent without a JS JSON round-trip.
    const getMarkingBlobForLaser = async () => {
        const data = {
            explorationId: explorationId,
            patchType: "SINGLE_CLUSTER",
            iterationNumber: iterationRef.current.number,
        };
        const response = await axios.post("api/download/marking/file", data, {
            responseType: "blob",
        });
        const id4 = explorationIdLast4(explorationId);
        const populationCode =
            populationCodeForPhase(
                iterationRef.current.phase,
                iterationRef.current.display
            ) || `iteration_${iterationRef.current.number}`;
        const filename =
            getFilenameFromContentDisposition(response.headers?.["content-disposition"]) ||
            `cluster_marking_file_${populationCode}_${id4}.json`;
        return { blob: response.data, filename };
    };

    const handlePreviewMarkingClick = async () => {
        setPreviewMarkingLoading(true);
        const previewWindow = window.open("", "_blank", "width=1100,height=900");
        if (previewWindow) {
            previewWindow.document.open();
            previewWindow.document.write("<p style=\"font-family:Arial,sans-serif;padding:16px;\">Generating marking preview...</p>");
            previewWindow.document.close();
        }
        try {
            const data = {
                explorationId: explorationId,
                patchType: "SINGLE_CLUSTER",
                iterationNumber: iterationRef.current.number,
                previewOnly: true,
            };
            const response = await axios.post("api/download/marking/file", data, {
                responseType: "blob",
            });
            const populationCodeForTitle =
                populationCodeForPhase(
                    iterationRef.current.phase,
                    iterationRef.current.display
                ) || `iteration ${iterationRef.current.number}`;
            openMarkingPreviewWindow(
                response.data,
                `Cluster marking preview - ${populationCodeForTitle}`,
                previewWindow
            );
        } catch (error) {
            console.error("Error previewing marking file", error);
            if (previewWindow && !previewWindow.closed) {
                previewWindow.document.open();
                previewWindow.document.write("<p style=\"font-family:Arial,sans-serif;padding:16px;color:#b91c1c;\">Could not load marking preview.</p>");
                previewWindow.document.close();
            }
        } finally {
            setPreviewMarkingLoading(false);
        }
    };

    const getIterationNumber = async () => {
        const request_data = { "explorationId": explorationId };
        try {
            const response = await axios.post('api/exploration/iteration/number', request_data);
            applyIterationResponse(response);
            getPatchDetails();
            fetchMarkingTime();
        } catch (error) {
            console.error('Error:', error);
        }
    };
    // Predicted marking time of this iteration's cluster marking file, asked
    // for as soon as the iteration is known (timeOnly builds no file).
    const fetchMarkingTime = async () => {
        try {
            const response = await axios.post("api/download/marking/file", {
                explorationId,
                patchType: "SINGLE_CLUSTER",
                iterationNumber: iterationRef.current.number,
                timeOnly: true,
            });
            setMarkingTime(response.data?.marking_time || null);
        } catch (error) {
            setMarkingTime(null);
        }
    };

    const getPatchDetails = async () => {
        const request_data = {
            "explorationId": explorationId,
            "iterationNumber" : iterationRef.current.number,
            "isWidthPatchGroup" : true

         };
        axios.post('api/patch/index/detail', request_data)
            .then(response => {
                setTotalBatches(response.data.totalBatchCount);
                setCurrentPatchIndex(response.data.currentIndex);
            })
            .catch(error => {
                console.error('Error:', error);
            });
    }

    useEffect(() => {
        getIterationNumber();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
    }, []);

    useEffect(() => {
        const n = Math.max(0, Number(totalBatches) || 0);
        if (n <= 0) return;
        setGeometricSlots((prev) => {
            if (prev.length === n) return prev;
            return Array.from({ length: n }, (_, i) => prev[i] ?? emptyGeometricSlot());
        });
    }, [totalBatches]);

    useEffect(() => {
        if (modalSlotIndex == null) return;
        const onResize = () => {
            window.requestAnimationFrame(() => setModalImageLayoutTick((v) => v + 1));
        };
        window.addEventListener("resize", onResize);
        onResize();
        return () => window.removeEventListener("resize", onResize);
    }, [modalSlotIndex]);

    const getScaledCoordinatesForSmallImage = (batchIndex, x_a, y_a) => {
        const el = slotImageRefs.current[batchIndex];
        return naturalToDisplayedImageCoords(el, x_a, y_a);
    };

    const allGeometricSlotsReady =
        geometricSlots.length > 0 &&
        geometricSlots.every(
            (s) =>
                s.previewUrl &&
                s.sourceFile &&
                s.cornerPoints.length === 4 &&
                s.scalingFactors &&
                Number.isFinite(s.scalingFactors.xScale) &&
                Number.isFinite(s.scalingFactors.yScale)
        );

    const modalSlot = modalSlotIndex != null ? geometricSlots[modalSlotIndex] : null;

    return (
        <>
        <Box
            sx={{
                width: "100%",
                maxWidth: "100%",
                mt: "2%",
                mb: 4,
                boxSizing: "border-box",
            }}
        >
            <ExplorationPageShell
                title="Geometric Measurement"
                subtitle="Upload a microscope photo for each patch group, align corners, then continue once — all groups are sent before width measurement."
                noPaper
                stepSidebar={{
                    steps: EXPLORATION_FLOW_STEPS,
                    currentIndex: 0,
                    iterationValue: explorationIteration,
                    populationLabel: populationLabelForPhase(
                        iterationPhase,
                        explorationIteration
                    ),
                }}
                contentBoxSx={{
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    /* Match gradient header width: shell already has px:2; extra px here made step cards narrower */
                    px: 0,
                    py: { xs: 2, md: 3 },
                }}
            >
                <Stack spacing={3} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, mt: 1, alignItems: "stretch" }}>
                    <Paper elevation={0} sx={stepCardSx}>
                        <Stack spacing={1.5} sx={{ width: "100%" }}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
                                <Box
                                    sx={{
                                        minWidth: 28,
                                        height: 28,
                                        borderRadius: "50%",
                                        bgcolor: "primary.main",
                                        color: "primary.contrastText",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        typography: "subtitle2",
                                        fontWeight: 700,
                                    }}
                                >
                                    1
                                </Box>
                                <Box
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        Mark the cluster patches
                                    </Typography>
                                    <MarkingOptionsBox
                                        predictedMarkingTime={markingTime}
                                        sx={{ mt: 1.5 }}
                                        remote={
                                            <LaserMarkPanel
                                                getMarkingBlob={getMarkingBlobForLaser}
                                                sectionTitle="Connected laser"
                                                markingContext={{
                                                    explorationId: explorationId,
                                                    iterationNumber: iterationRef.current.number,
                                                    patchType: "SINGLE_CLUSTER",
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Marks through the laser software on this computer.
                                                </Typography>
                                            </LaserMarkPanel>
                                        }
                                    >
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Cluster marking JSON for your laser software. Preview opens in a separate
                                            window.
                                        </Typography>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                disableElevation
                                                disabled={downloadMarkingLoading}
                                                startIcon={<DownloadIcon />}
                                                onClick={handleDownloadClick}
                                            >
                                                Download marking JSON
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                disabled={previewMarkingLoading}
                                                startIcon={<PreviewIcon />}
                                                onClick={handlePreviewMarkingClick}
                                            >
                                                Preview marking
                                            </Button>
                                            {downloadMarkingLoading || previewMarkingLoading ? (
                                                <CircularProgress size={20} thickness={4} aria-label="Marking file action" />
                                            ) : null}
                                        </Stack>
                                    </MarkingOptionsBox>
                                </Box>
                            </Stack>
                        </Stack>
                    </Paper>

                    <Paper elevation={0} sx={stepCardSx}>
                        <Stack spacing={3} sx={{ width: "100%" }}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
                                <Box
                                    sx={{
                                        minWidth: 28,
                                        height: 28,
                                        borderRadius: "50%",
                                        bgcolor: "primary.main",
                                        color: "primary.contrastText",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        typography: "subtitle2",
                                        fontWeight: 700,
                                    }}
                                >
                                    2
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        Upload microscope images
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        One photo per patch group for this iteration. Labels in each photo must match{" "}
                                        <strong>[iteration][batch]</strong> for that group. When every group is ready, use Next
                                        to run geometry for all groups and open width measurement.
                                    </Typography>
                                </Box>
                            </Stack>

                            {geometricSlots.length === 0 ? null : (
                                geometricSlots.map((slot, batchIndex) => (
                                    <Stack
                                        key={batchIndex}
                                        spacing={2}
                                        sx={{
                                            width: "100%",
                                            pt: batchIndex > 0 ? 2 : 0,
                                            borderTop: batchIndex > 0 ? "1px solid" : "none",
                                            borderColor: batchIndex > 0 ? "divider" : "transparent",
                                        }}
                                    >
                                        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                                            Patch group {batchIndex + 1} of {geometricSlots.length}
                                        </Typography>
                                        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
                                            <Box
                                                aria-hidden
                                                sx={{
                                                    minWidth: 28,
                                                    width: 28,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "flex-start",
                                                }}
                                            >
                                                <CameraCapturePanel
                                                    label="Capture cluster photo"
                                                    disabled={slot.previewLoading || loading}
                                                    onCaptured={(file) => processMicroscopeFileForSlot(batchIndex, file)}
                                                    manualUpload={
                                                        <Button
                                                            component="label"
                                                            variant="outlined"
                                                            size="small"
                                                            disableElevation
                                                            disabled={slot.previewLoading || loading}
                                                            startIcon={<UploadFileIcon />}
                                                        >
                                                            Manual upload
                                                            <input
                                                                hidden
                                                                type="file"
                                                                disabled={slot.previewLoading || loading}
                                                                onChange={(e) => handleMicroscopeSlotImageChange(batchIndex, e)}
                                                            />
                                                        </Button>
                                                    }
                                                />
                                            </Box>
                                        </Stack>

                                        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
                                            <Box
                                                aria-hidden
                                                sx={{
                                                    minWidth: 28,
                                                    width: 28,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Stack
                                                direction="row"
                                                spacing={1.5}
                                                alignItems="flex-start"
                                                flexWrap="wrap"
                                                sx={{ flex: 1, minWidth: 0 }}
                                            >
                                                <Box
                                                    role={slot.previewUrl && !slot.previewLoading ? "button" : undefined}
                                                    tabIndex={slot.previewUrl && !slot.previewLoading ? 0 : undefined}
                                                    aria-label={
                                                        slot.previewUrl && !slot.previewLoading
                                                            ? "Open full screen to adjust corner points"
                                                            : undefined
                                                    }
                                                    onClick={
                                                        slot.previewUrl && !slot.previewLoading
                                                            ? () => {
                                                                  setModalSlotIndex(batchIndex);
                                                              }
                                                            : undefined
                                                    }
                                                    onKeyDown={
                                                        slot.previewUrl && !slot.previewLoading
                                                            ? (e) => {
                                                                  if (e.key === "Enter" || e.key === " ") {
                                                                      e.preventDefault();
                                                                      setModalSlotIndex(batchIndex);
                                                                  }
                                                              }
                                                            : undefined
                                                    }
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        maxWidth: 440,
                                                        aspectRatio: "1",
                                                        position: "relative",
                                                        borderRadius: 2,
                                                        overflow: "hidden",
                                                        bgcolor: slot.previewUrl ? "grey.100" : "grey.50",
                                                        border: "1px dashed",
                                                        borderColor: slot.previewUrl ? "divider" : "text.disabled",
                                                        ...(slot.previewUrl && !slot.previewLoading
                                                            ? {
                                                                  cursor: "pointer",
                                                                  transition: "border-color 0.15s ease, border-style 0.15s ease",
                                                                  "&:hover": {
                                                                      borderColor: "primary.main",
                                                                      borderStyle: "solid",
                                                                  },
                                                              }
                                                            : {}),
                                                    }}
                                                >
                                                    {slot.previewLoading && (
                                                        <Box
                                                            sx={{
                                                                position: "absolute",
                                                                inset: 0,
                                                                zIndex: 4,
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                gap: 1.5,
                                                                px: 2,
                                                                bgcolor: "rgba(255, 255, 255, 0.88)",
                                                                backdropFilter: "blur(4px)",
                                                            }}
                                                        >
                                                            <Typography variant="body2" color="text.secondary" align="center">
                                                                {slot.previewMessage || "Processing…"}
                                                            </Typography>
                                                            <LinearProgress sx={{ width: "min(90%, 280px)" }} />
                                                        </Box>
                                                    )}
                                                    {slot.previewUrl ? (
                                                        <Box
                                                            sx={{
                                                                position: "absolute",
                                                                inset: 0,
                                                                /* Corner math from naturalToDisplayedImageCoords is relative to the
                                                                 * <img> box; keep img + dots in this wrapper so origins match (avoid
                                                                 * flex centering on the outer click target). */
                                                            }}
                                                        >
                                                            <img
                                                                src={slot.previewUrl}
                                                                ref={(el) => {
                                                                    slotImageRefs.current[batchIndex] = el;
                                                                }}
                                                                alt={`Microscope preview group ${batchIndex + 1}`}
                                                                style={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    objectFit: "contain",
                                                                    display: "block",
                                                                }}
                                                                onLoad={() => setThumbnailLayoutTick((t) => t + 1)}
                                                            />
                                                            {slot.cornerPoints.map((coordinate, index) => {
                                                                const scaledCoordinates = getScaledCoordinatesForSmallImage(
                                                                    batchIndex,
                                                                    coordinate.x_a,
                                                                    coordinate.y_a
                                                                );
                                                                return (
                                                                    <Box
                                                                        key={index}
                                                                        sx={{
                                                                            position: "absolute",
                                                                            top: scaledCoordinates.y,
                                                                            left: scaledCoordinates.x,
                                                                            width: "10px",
                                                                            height: "10px",
                                                                            borderRadius: "50%",
                                                                            bgcolor: "error.main",
                                                                            transform: "translate(-50%, -50%)",
                                                                            pointerEvents: "none",
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                position: "absolute",
                                                                inset: 0,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            <Typography variant="body2" color="text.disabled" sx={{ px: 2, textAlign: "center" }}>
                                                                Preview appears here after you choose a file
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                {slot.previewUrl && !slot.previewLoading && slot.cornerPoints.length !== 4 ? (
                                                    <Alert
                                                        severity="error"
                                                        variant="outlined"
                                                        role="status"
                                                        sx={(theme) => ({
                                                            flex: "1 1 220px",
                                                            maxWidth: 400,
                                                            py: 1.25,
                                                            px: 1.5,
                                                            borderRadius: 2,
                                                            alignItems: "flex-start",
                                                            borderColor: alpha(theme.palette.error.main, 0.28),
                                                            bgcolor: alpha(theme.palette.error.main, 0.06),
                                                            color: "text.primary",
                                                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                                            "& .MuiAlert-icon": {
                                                                color: theme.palette.error.main,
                                                                opacity: 0.95,
                                                            },
                                                            "& .MuiAlert-message": {
                                                                width: "100%",
                                                                paddingTop: "2px",
                                                            },
                                                        })}
                                                    >
                                                        <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.55, m: 0 }}>
                                                            Place exactly four corner points ({slot.cornerPoints.length} selected).
                                                            Open the preview to adjust.
                                                        </Typography>
                                                    </Alert>
                                                ) : null}
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                ))
                            )}
                        </Stack>
                    </Paper>

                    <Divider sx={{ width: "100%" }} />

                    <Box sx={{ width: "100%", pt: 1 }}>
                        <NavBarBottom
                            variant="embedded"
                            prevPage={EXPLORATION_LIST_ROUTE}
                            nextPage={stepRoute("width")}
                            hideReturnButton={
                                !(Number(iteration.number) > 0)
                            }
                            leadingSlot={
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    disableElevation
                                    sx={navBottomButtonSx}
                                    onClick={handleCancelToExplorationsList}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                            }
                            leftSlot={
                                Number(iteration.number) > 0 ? (
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<UndoIcon />}
                                        sx={navBottomButtonSx}
                                        onClick={handleOpenRedoMeasurementDialog}
                                        disabled={loading || rollbackBusy}
                                    >
                                        Rollback last iteration
                                    </Button>
                                ) : null
                            }
                            enableNextPage={!allGeometricSlotsReady}
                            nextPageClickFunc={nextPageClickFunc}
                            nextLoading={loading}
                        />
                    </Box>
                </Stack>
            </ExplorationPageShell>
        </Box>

            {/* Modal for manual corner adjustment */}
            <Modal
                open={modalSlotIndex !== null}
                onClose={handleCloseModal}
                aria-labelledby="corner-adjust-modal-title"
                BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.65)" } }}
            >
                <MuiBox
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "96vw", sm: "92vw" },
                        maxWidth: 1100,
                        height: "min(90vh, 920px)",
                        maxHeight: "90vh",
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        outline: "none",
                    }}
                >
                    <Typography
                        id="corner-adjust-modal-title"
                        component="h2"
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ flexShrink: 0, px: 2, pt: 2, pb: 1 }}
                    >
                        Adjust corner points
                        {modalSlotIndex != null && geometricSlots.length > 1
                            ? ` — patch group ${modalSlotIndex + 1} of ${geometricSlots.length}`
                            : null}
                    </Typography>
                    {modalSlot?.previewUrl ? (
                        <Box
                            sx={{
                                position: "relative",
                                flex: 1,
                                minHeight: 0,
                                mx: 2,
                                mb: 0,
                                bgcolor: "grey.100",
                                borderRadius: 1,
                                overflow: "hidden",
                            }}
                        >
                            {/*
                             * Native <img> so modalImageRef is always an HTMLImageElement with naturalWidth/
                             * naturalHeight. Otherwise naturalToDisplayedImageCoords returns (0,0) and every
                             * marker stacks at the top-left of the overlay.
                             */}
                            <Box sx={{ position: "absolute", inset: 0 }}>
                                <img
                                    ref={modalImageRef}
                                    src={modalSlot.previewUrl}
                                    alt=""
                                    onLoad={bumpModalImageLayout}
                                    onClick={handleImageClick}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        display: "block",
                                        cursor: "crosshair",
                                        margin: 0,
                                        verticalAlign: "top",
                                    }}
                                />
                                <Box
                                    key={modalImageLayoutTick}
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        pointerEvents: "none",
                                        zIndex: 1,
                                    }}
                                >
                                    {(modalSlot.cornerPoints ?? []).map((coordinate, index) => {
                                        const p = naturalToDisplayedImageCoords(
                                            modalImageRef.current,
                                            coordinate.x_a,
                                            coordinate.y_a
                                        );
                                        return (
                                            <Box
                                                key={index}
                                                sx={{
                                                    position: "absolute",
                                                    top: p.y,
                                                    left: p.x,
                                                    width: "10px",
                                                    height: "10px",
                                                    borderRadius: "50%",
                                                    bgcolor: "error.main",
                                                    transform: "translate(-50%, -50%)",
                                                    pointerEvents: "none",
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Box>
                    ) : null}
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="flex-end"
                        alignItems="center"
                        flexWrap="wrap"
                        sx={{
                            flexShrink: 0,
                            px: 2,
                            py: 2,
                            borderTop: 1,
                            borderColor: "divider",
                            bgcolor: "background.paper",
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleClearCornerPoints}
                            disabled={automaticCornerDetectionBusy}
                            sx={actionButtonSx}
                        >
                            Clear corner points
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleAutomaticCornerDetection}
                            disabled={
                                automaticCornerDetectionBusy ||
                                !(modalSlot?.sourceFile instanceof File) ||
                                !modalSlot?.fileExtension
                            }
                            sx={actionButtonSx}
                        >
                            {automaticCornerDetectionBusy ? (
                                <CircularProgress size={22} thickness={4} aria-label="Detecting corners" />
                            ) : (
                                "Automatic Detection"
                            )}
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation
                            disabled={(modalSlot?.cornerPoints?.length ?? 0) !== 4 || automaticCornerDetectionBusy}
                            title={
                                automaticCornerDetectionBusy
                                    ? "Wait for automatic detection to finish."
                                    : (modalSlot?.cornerPoints?.length ?? 0) !== 4
                                      ? "Select exactly four corner points on the image before saving."
                                      : undefined
                            }
                            onClick={handleCloseModal}
                            sx={actionButtonSx}
                        >
                            Save
                        </Button>
                    </Stack>
                </MuiBox>
            </Modal>
            <Stack direction="row" spacing={1} justifyContent="center"
                    alignItems="center">
                    <Dialog
                        open={redoMeasurement}
                        onClose={handleCloseRedoMeasurementDialog}
                        aria-labelledby="rollback-last-iteration-title"
                        aria-describedby="rollback-last-iteration-description"
                    >
                        <DialogTitle id="rollback-last-iteration-title">
                            Rollback last iteration?
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id="rollback-last-iteration-description">
                                This deletes <strong>everything</strong> from the
                                most recently completed iteration — its uploaded
                                cluster image, the cluster width measurements,
                                the color measurements, and the iteration row
                                that was created for the next iteration. You'll
                                land back at the start of the previous iteration
                                with a fresh Geometric Measurement step, where
                                you can re-upload the cluster image. This
                                cannot be undone.
                            </DialogContentText>
                            {rollbackError && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {rollbackError}
                                </Alert>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, pb: 2 }}>
                            <Button
                                onClick={handleCloseRedoMeasurementDialog}
                                variant="outlined"
                                color="primary"
                                disabled={rollbackBusy}
                                sx={dialogButtonSx}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleConfirmRedoMeasurement}
                                variant="contained"
                                color="error"
                                autoFocus
                                disabled={rollbackBusy}
                                startIcon={
                                    rollbackBusy ? (
                                        <CircularProgress
                                            size={16}
                                            thickness={5}
                                            sx={{ color: "inherit" }}
                                        />
                                    ) : null
                                }
                                sx={dialogButtonSx}
                            >
                                {rollbackBusy ? "Rolling back…" : "Rollback iteration"}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Stack>
            <Stack direction="row" spacing={1} justifyContent="center"
                    alignItems="center">
                    <Dialog
                        open={showWrongMeasurementData}
                        onClose={handleCloseErrorDialog}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">{"Attention!"}</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description" component="div">
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    The server could not confirm the printed labels{" "}
                                    <strong>[iteration][batch]</strong> in your photo (automatic check before upload).
                                    The photo must clearly show the same labels as the current exploration step (e.g.{" "}
                                    <strong>[0][0]</strong> for iteration&nbsp;0, batch&nbsp;0).
                                </Typography>
                                {uploadValidationError?.Expected != null ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Expected: iteration{" "}
                                        <strong>{uploadValidationError.Expected.iteration}</strong>, batch{" "}
                                        <strong>{uploadValidationError.Expected.batch}</strong>.
                                        {Array.isArray(uploadValidationError.Seen) && uploadValidationError.Seen.length > 0 ? (
                                            <>
                                                {" "}
                                                OCR read:{" "}
                                                {uploadValidationError.Seen.map((c) => c.text).filter(Boolean).join("; ") ||
                                                    "(no text matched)"}
                                            </>
                                        ) : (
                                            <> No label text was read from the image (lighting, angle, or crop).</>
                                        )}
                                    </Typography>
                                ) : null}
                                {uploadValidationError?.error ? (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {uploadValidationError.error}
                                    </Typography>
                                ) : null}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, pb: 2 }}>
                            <Button onClick={handleCloseErrorDialog} color="inherit">
                                Close
                            </Button>
                            <Button
                                onClick={handleContinueWithoutIdentifierCheck}
                                variant="contained"
                                color="primary"
                                disabled={loading}
                            >
                                Continue anyway — I confirm this image is correct
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Stack>
            <Dialog
                open={Boolean(submitError)}
                onClose={handleCloseSubmitError}
                aria-labelledby="cluster-submit-error-title"
                aria-describedby="cluster-submit-error-description"
            >
                <DialogTitle id="cluster-submit-error-title">
                    {submitError?.flavour === "timedOut"
                        ? "Cluster measurement timed out"
                        : submitError?.flavour === "precondition"
                          ? "Cluster measurement rejected"
                          : "Cluster measurement failed"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="cluster-submit-error-description" component="div">
                        {submitError?.flavour === "timedOut" ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                The cluster measurement is taking longer than the proxy timeout. The
                                backend may still be running. We polled for a result but did not see
                                completion in time. You can retry now, or wait a moment and click
                                &quot;Next&quot; again — the iteration is not lost.
                            </Typography>
                        ) : submitError?.flavour === "precondition" ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                The server refused the request because the iteration is not in the
                                expected state for cluster measurement. Reload the exploration from
                                the home page to continue from the correct step.
                            </Typography>
                        ) : (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                The cluster measurement step did not finish successfully. The
                                iteration may be partially measured; you can retry from this batch
                                or rollback the iteration on the width review page.
                            </Typography>
                        )}
                        {submitError?.message ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                                {submitError.status ? `HTTP ${submitError.status}: ` : ""}
                                {submitError.message}
                                {submitError.code ? ` (${submitError.code})` : ""}
                            </Typography>
                        ) : null}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, pb: 2 }}>
                    <Button onClick={handleCloseSubmitError} color="inherit">
                        Close
                    </Button>
                    <Button
                        onClick={handleRetrySubmit}
                        variant="contained"
                        color="primary"
                        disabled={loading || submitRecoveryBusy}
                    >
                        {loading || submitRecoveryBusy ? "Working…" : "Retry"}
                    </Button>
                </DialogActions>
            </Dialog>
            <IterationExitDialog
                open={cancelDialogOpen}
                onClose={handleCloseCancelDialog}
                onDiscardAndExit={handleDiscardAndExit}
                summary={
                    totalBatches > 0 && currentPatchIndex > 0
                        ? `You've already measured ${Math.min(
                              currentPatchIndex,
                              totalBatches
                          )} of ${totalBatches} cluster patch group(s) in this iteration.`
                        : ""
                }
                stepLabel="this iteration's cluster width measurements"
                discardLabel="Discard width measurements and exit"
            />
        </>
    );
}
